import { Provider, Viewport } from "@radix-ui/react-toast";
import { type FC, createContext, useCallback, useContext, useState } from "react";

import { Box, ThemeWrapper, Toast, type ToastProps } from "@theme/components";

import { hasArrayValues } from "@theme/utils";

type AddToastProps = Omit<ToastProps, "id">;

interface ToastContextProps {
  addToast: (props: AddToastProps) => void;
  /**
   * Remove a toast by id
   * If no id is provided, remove all toasts
   */
  removeToast: (id?: string) => void;
}

export const ToastContext = createContext({} as ToastContextProps);

export const ToastProvider: FC = ({ children }) => {
  const [activeToasts, setActiveToasts] = useState<ToastProps[]>([]);

  const addToast = useCallback(
    (toast: AddToastProps) => {
      setActiveToasts((toasts) => [
        ...toasts,
        {
          ...toast,
          id: crypto.randomUUID(),
          visible: true,
        },
      ]);
    },
    [setActiveToasts],
  );

  /**
   * Remove a toast by id
   * If no id is provided, remove all toasts
   */
  const removeToast: ToastContextProps["removeToast"] = useCallback(
    (id) => {
      if (id) {
        setActiveToasts((toasts) => toasts.filter((toast) => toast.id !== id));
      } else {
        setActiveToasts([]);
      }
    },
    [setActiveToasts],
  );

  const handleRadixOpenChange = (id: string) => (open: boolean) => {
    if (open) return;
    setActiveToasts((prev) => prev.map((t) => (t.id === id ? { ...t, visible: false } : t)));
  };

  return (
    <ToastContext.Provider value={{ addToast, removeToast }}>
      <Provider>
        {children}
        {hasArrayValues(activeToasts) &&
          activeToasts.map((toast) => (
            <Toast
              key={toast.id}
              open
              onOpenChange={handleRadixOpenChange(toast.id)}
              onExited={() => removeToast(toast.id)}
              {...toast}
            />
          ))}
        <ThemeWrapper>
          <Viewport data-plump asChild>
            <Box
              sx={{
                position: "fixed",
                right: ["s400", "s500"],
                left: ["s400", "unset"],
                bottom: "s400",
                width: [null, "full"],
                maxWidth: [null, 300],
                listStyle: "none",
                zIndex: 999999999,
              }}
            />
          </Viewport>
        </ThemeWrapper>
      </Provider>
    </ToastContext.Provider>
  );
};

export const useToast = () => useContext(ToastContext);
